import axiosInstance from "@/lib/axiosInstance";
import { contactsEndpoints } from "./contacts.endpoints";
import type {
  ContactsListQueryOptions,
  ContactsListResponse,
  ContactResponse,
  LocationDropdownOption,
  ContactDropdownOption,
  DropdownResponse,
} from "./contacts.queries.types";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";

export const ContactsQueryKeys = {
  list: "contactsList",
  detail: "contactDetail",
  locationsDropdown: "contactsLocationsDropdown",
  contactsDropdown: "contactsContactsDropdown",
} as const;

export const getContactsList = async (
  clientId: string,
  options: ContactsListQueryOptions,
  signal: AbortSignal,
): Promise<ContactsListResponse> => {
  const params = new URLSearchParams();

  params.append("page", options.page.toString());
  params.append("limit", options.limit.toString());

  if (options.search) {
    params.append("search", options.search);
  }

  if (options.sortBy) {
    params.append("sortBy", options.sortBy);
  }

  if (options.sortOrder) {
    params.append("sortOrder", options.sortOrder);
  }

  if (options.filters && Object.keys(options.filters).length > 0) {
    const filterObj: Record<string, string[]> = {};

    Object.entries(options.filters).forEach(([filterBy, values]) => {
      if (values && values.length > 0) {
        filterObj[filterBy] = values;
      }
    });

    params.append("filterBy", JSON.stringify(filterObj));
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);
  const fullUrl = `${contactsEndpoints.getContacts(clientId)}?${queryString}`;

  const response = await axiosInstance.get<ContactsListResponse>(fullUrl, {
    signal,
  });

  return response.data;
};

export const getContact = async (
  clientId: string,
  contactId: string,
  signal: AbortSignal,
): Promise<ContactResponse> => {
  const { data } = await axiosInstance.get<ContactResponse>(
    contactsEndpoints.getContact(clientId, contactId),
    {
      signal,
    },
  );

  return data;
};

export const getLocationsDropdown = async (
  clientId: string,
  signal: AbortSignal,
): Promise<DropdownResponse<LocationDropdownOption>> => {
  const params = new URLSearchParams();
  params.append("type", "ClientLocations");
  params.append("field", "siteId");
  params.append("clientId", clientId);
  const queryString = params.toString();
  const url = `${contactsEndpoints.dropdown}${queryString ? `?${queryString}` : ""}`;
  const { data } = await axiosInstance.get<DropdownResponse<LocationDropdownOption>>(url, {
    signal,
  });
  return data;
};

export const getContactsDropdown = async (
  clientId: string,
  signal: AbortSignal,
  excludeContactId?: string | null,
): Promise<DropdownResponse<ContactDropdownOption>> => {
  const params = new URLSearchParams();
  params.append("type", "ClientContacts");
  params.append("field", "firstName");
  params.append("field", "lastName");
  params.append("field", "id");
  params.append("filterBy", "clientId");
  params.append("filterValue", clientId);
  if (excludeContactId?.trim()) {
    params.append("excludeId", JSON.stringify([excludeContactId.trim()]));
  }
  const queryString = params.toString();
  const url = `${contactsEndpoints.dropdown}${queryString ? `?${queryString}` : ""}`;
  const { data } = await axiosInstance.get<DropdownResponse<ContactDropdownOption>>(url, {
    signal,
  });
  return data;
};

// General contacts dropdown function for table filters
export const getContactsTableDropdown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
  searchParams?: TSearchParams,
): Promise<any> => {
  const params = new URLSearchParams();
  const clientId = searchParams?.clientId;

  if (search) {
    params.append("search", search);
  }

  // /dropdown?type=ClientContacts&field=firstName&field=lastName&field=id&search=Hari
  if (key === "fullName") {
    params.append("type", "ClientContacts");
    params.append("field", "firstName");
    params.append("field", "lastName");
    params.append("field", "id");
    params.append("distinct", "true");
    if (clientId) {
      params.append("filterBy", "clientId");
      params.append("filterValue", clientId);
    }
    params.append("modify", `{"source":["firstName","lastName"],"target":"label"}`);
  }

  // /dropdown?type=ClientContacts&field=jobTitle&filterBy=clientId&filterValue=01KBYAS14Y2XPTBKK68VPZ0VRF&distinct=true
  if (key === "jobTitle") {
    params.append("type", "ClientContacts");
    params.append("field", "jobTitle");
    if (clientId) {
      params.append("filterBy", "clientId");
      params.append("filterValue", clientId);
    }
    params.append("distinct", "true");
  }

  // /dropdown?type=ClientLocations&field=siteId
  if (key === "locationsManaged") {
    params.append("type", "ClientLocations");
    params.append("field", "siteId");
    if (searchParams?.filterValue) {
      params.append("filterBy", "id");
      params.append("filterValue", searchParams.filterValue);
    } else if (clientId) {
      params.append("clientId", clientId);
    }

    const queryString = getQueryParamsWithCustomQueryObj(params);
    const response = await axiosInstance.get<any>(`${contactsEndpoints.dropdown}?${queryString}`, {
      signal,
    });

    const items = Array.isArray(response.data?.data) ? response.data.data : response.data;
    if (Array.isArray(items)) {
      for (const item of items) {
        if (!item) continue;
        if (!item.label) {
          item.label = item.siteId ?? item.id ?? "";
        }
        item.value = item.siteId ?? item.id ?? "";

        const siteId = item.siteId ?? "";
        const label = item.label ?? "";
        if (siteId && label.startsWith(`${siteId} - `)) {
          item.tooltip = label.slice(siteId.length + 3);
        } else if (label && label !== siteId) {
          item.tooltip = label;
        }
      }
    }

    return response.data;
  }

  // /dropdowns/client-contact-details?clientId=01HX5ABC&type=phone&search=555
  if (key === "phoneNumbers") {
    if (!clientId) {
      console.error("clientId is required for phoneNumbers dropdown");
      return "";
    }
    params.append("clientId", clientId);
    params.append("type", "phone");

    // search is already appended above if provided
    const queryString = getQueryParamsWithCustomQueryObj(params);
    const response = await axiosInstance.get<any>(
      `${contactsEndpoints.clientContactDetailsDropdown}?${queryString}`,
      { signal },
    );
    return response.data;
  }

  // /dropdown?type=ClientContacts&field=firstName&field=lastName&field=id&filterBy=clientId&filterValue={{valid_client_id}}
  if (key === "contactsManaged") {
    params.append("type", "ClientContacts");
    params.append("field", "firstName");
    params.append("field", "lastName");
    params.append("field", "id");
    params.append("modify", `{"source":["firstName","lastName"],"target":"label"}`);
    if (clientId) {
      params.append("filterBy", "clientId");
      params.append("filterValue", clientId);
    }
  }

  // /dropdown/lookups?type=sms_status
  if (key === "sms") {
    params.append("type", "sms_status");
    const queryString = getQueryParamsWithCustomQueryObj(params);
    const response = await axiosInstance.get<any>(`${contactsEndpoints.lookups}?${queryString}`, {
      signal,
    });
    return response.data;
  }

  // /dropdowns/client-contact-details?clientId=01HX5ABC&type=email
  if (key === "emailAddresses") {
    if (!clientId) {
      console.error("clientId is required for emailAddresses dropdown");
      return "";
    }
    params.append("clientId", clientId);
    params.append("type", "email");
    // search is already appended above if provided
    const queryString = getQueryParamsWithCustomQueryObj(params);
    const response = await axiosInstance.get<any>(
      `${contactsEndpoints.clientContactDetailsDropdown}?${queryString}`,
      { signal },
    );
    return response.data;
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  const response = await axiosInstance.get<any>(`${contactsEndpoints.dropdown}?${queryString}`, {
    signal,
  });

  return response.data;
};
